import { memo, useMemo } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { KentuckyKYC, SeonPlugin } from "@leapfinancial/kentucky-sdk";
import { config } from "../config";
import { APP_ROUTES } from "../constants";
import { useSessionStore } from "../store/useSessionStore";
import { useRouteWithToken } from "../hooks/useRouteWithToken";
import { useConsumedLinkGuard } from "../hooks/useConsumedLinkGuard";
import { useKentuckySdkStyles } from "../hooks/useKentuckySdkStyles";
import { forgetCipTicketId, rememberCipTicketId } from "../utils/chunkReload";

interface CipPageLocationState {
  cipTicketId: string;
}

/**
 * Embedded Kentucky CIP. Ticket arrives via router state only (never the URL).
 * Plugins: SeonPlugin only — host already owns PostHog init/identify.
 */
export const CipPage = memo(() => {
  useKentuckySdkStyles();
  useConsumedLinkGuard();
  const navigate = useNavigate();
  const { redirectTo } = useRouteWithToken();
  const location = useLocation();
  const { t } = useTranslation();
  const { session } = useSessionStore();

  const state = location.state as CipPageLocationState | null;
  const cipTicketId = rememberCipTicketId(state?.cipTicketId);

  const partnerTheme = session.data?.partner?.theme;
  const plugins = useMemo(() => [SeonPlugin()], []);

  if (!cipTicketId) {
    navigate(APP_ROUTES.ERROR_PAGE, {
      state: {
        title: t("error.title"),
        subTitle: null,
        message: t("error.tryAgainLater"),
        btnText: t("common.retry"),
        isOut: false,
        pageUrl: APP_ROUTES.SUMMARY_PAGE,
      },
    });
    return null;
  }

  return (
    <KentuckyKYC
      ticketId={cipTicketId}
      apiBaseUrl={config.KENTUCKY_API_BASE_URL}
      googleMapsApiKey={config.GOOGLE_MAP_API_KEY}
      awsRegion={config.AWS_REGION || "us-east-1"}
      theme={partnerTheme}
      plugins={plugins}
      onComplete={() => {
        forgetCipTicketId();
        redirectTo(APP_ROUTES.SENDING_MONEY_PAGE, { replace: true });
      }}
      onError={(error) => {
        navigate(APP_ROUTES.ERROR_PAGE, {
          state: {
            title: t("error.title"),
            subTitle: null,
            message:
              error.message || t("error.tryAgainLater"),
            btnText: t("common.retry"),
            isOut: false,
            pageUrl: APP_ROUTES.SUMMARY_PAGE,
          },
        });
      }}
    />
  );
});
